import { useMutation, useQuery } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { getIntl } from '../../utils/intl';
import { useClearInMemoryAvatarCache } from '../useAvatar';
import { appAPI } from '../../utils/api';
import { getRelatedCareTeamMemberCacheKey, onCareTeamUpdate, onRelatedCareTeamMemberUpdate } from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import { useHasPermission } from '../../components/v2BaseComponents/Permissions/hooks';

const intl = getIntl('useCareTeamMember', {
    careTeamMemberLoadFailure: 'Failed to load care team member',
    updateSuccess: 'Successfully updated care team member',
    updateFailure: 'Failed to update care team member',
    avatarUpdateFailure: 'Failed to update care team member avatar',
    avatarDeleteFailure: 'Failed to delete care team member avatar',
    relatedCareTeamMembersLoadFailure: 'Failed to load related care team members',
    addRelatedCareTeamMemberSuccess: 'Successfully added related care team member',
    addRelatedCareTeamMemberFailure: 'Failed to add related care team member',
    removeRelatedCareTeamMemberSuccess: 'Successfully removed related care team member',
    removeRelatedCareTeamMemberFailure: 'Failed to remove related care team member',
    copyCareTeamMemberSuccess: 'Successfully copied care team member',
    copyCareTeamMemberFailure: 'Failed to copy care team member',
});

export type UseCareTeamMemberArgs = {
    medicalProfileId: string;
    careTeamMemberId: string | null;
    onUpdateCareTeamMemberSuccess?: () => void;
    onAddRelatedCareTeamMemberSuccess?: () => void;
    onCopyCareTeamMemberSuccess?: (destinationCareTeamMemberId: string) => void;
};

export type UseCareTeamMemberReturnValue = {
    careTeamMember?: APITypes.medicalProfileService.GetCareTeamMemberResponse;
    relatedCareTeamMembers: APITypes.medicalProfileService.ListRelatedCareTeamMembersResponse;
    isLoadingRelatedCareTeamMembers: boolean;
    isLoadingCareTeamMember: boolean;
    updateCareTeamMember: (careTeamMember: APITypes.medicalProfileService.UpdateCareTeamMemberRequest) => void;
    isUpdatingCareTeamMember: boolean;
    removeRelatedCareTeamMember: (relatedCareTeamMemberId: string) => void;
    isRemovingRelatedCareTeamMember: boolean;
    addRelatedCareTeamMember: (relatedCareTeamMemberId: string) => void;
    isAddingRelatedCareTeamMember: boolean;
    copyCareTeamMember: (destinationMedicalProfile: string) => void;
    isCopyingCareTeamMember: boolean;
};

export default function useCareTeamMember({
    medicalProfileId,
    careTeamMemberId,
    onUpdateCareTeamMemberSuccess,
    onAddRelatedCareTeamMemberSuccess,
    onCopyCareTeamMemberSuccess,
}: UseCareTeamMemberArgs): UseCareTeamMemberReturnValue {
    const hasListPermission = useHasPermission(medicalProfileId, 'careTeam', 'view');
    const resetRelatedCareTeamMemberCache = onRelatedCareTeamMemberUpdate.resetCache({
        medicalProfileId,
        careTeamMemberId: careTeamMemberId || '',
    });
    const clearInMemoryAvatarCache = useClearInMemoryAvatarCache();
    const clearCareTeamMemberCache = onCareTeamUpdate.resetCache({ medicalProfileId });
    const notify = useNotify();
    const avatarUrl = urls.api.careTeamMembers.avatar(medicalProfileId, careTeamMemberId || '');
    const insuranceCardFrontUrl = urls.api.careTeamMembers.insuranceCard(
        medicalProfileId,
        careTeamMemberId || '',
        'front',
    );
    const insuranceCardBackUrl = urls.api.careTeamMembers.insuranceCard(
        medicalProfileId,
        careTeamMemberId || '',
        'back',
    );

    const careTeamMemberQuery = useQuery({
        queryKey: [medicalProfileId, careTeamMemberId],
        queryFn: async () => {
            if (!careTeamMemberId) {
                throw new Error('careTeamMemberId is required');
            }

            return await appAPI.get<APITypes.medicalProfileService.GetCareTeamMemberResponse>(
                urls.api.careTeamMembers.careTeamMember(medicalProfileId, careTeamMemberId),
                {
                    // It's possible that a visit references a care team member that has been deleted.
                    ignoreErrorStatus: [404],
                },
            );
        },
        enabled: hasListPermission && !!careTeamMemberId,
    });
    useQueryErrorNotify(careTeamMemberQuery, intl('careTeamMemberLoadFailure'));

    const updateCareTeamMember = useMutation({
        mutationFn: async (careTeamMember: APITypes.medicalProfileService.UpdateCareTeamMemberRequest) => {
            if (!careTeamMemberId) {
                throw new Error('careTeamMemberId is required');
            }

            return await appAPI.put(urls.api.careTeamMembers.careTeamMember(medicalProfileId, careTeamMemberId), {
                body: careTeamMember,
            });
        },
        onSuccess: () => {
            onUpdateCareTeamMemberSuccess?.();
            clearCareTeamMemberCache();
            notify('success', intl('updateSuccess'));
            [avatarUrl, insuranceCardFrontUrl, insuranceCardBackUrl].forEach((url) => {
                clearInMemoryAvatarCache(url);
            });
        },
        onError: () => {
            notify('error', intl('updateFailure'));
        },
    });

    const relatedCareTeamMembersQuery = useQuery({
        queryKey: getRelatedCareTeamMemberCacheKey(medicalProfileId, careTeamMemberId || ''),
        queryFn: async () => {
            if (!careTeamMemberId) {
                throw new Error('careTeamMemberId is required');
            }

            return await appAPI.get(
                urls.api.careTeamMembers.relatedCareTeamMembers(medicalProfileId, careTeamMemberId),
            );
        },
        enabled: hasListPermission && !!careTeamMemberId,
    });
    useQueryErrorNotify(relatedCareTeamMembersQuery, intl('relatedCareTeamMembersLoadFailure'));

    const removeRelatedCareTeamMember = useMutation({
        mutationFn: async (relatedCareTeamMemberId: string) => {
            if (!careTeamMemberId) {
                throw new Error('careTeamMemberId is required');
            }

            await appAPI.del(
                urls.api.careTeamMembers.relatedCareTeamMembers(
                    medicalProfileId,
                    careTeamMemberId,
                    relatedCareTeamMemberId,
                ),
            );
        },
        onSuccess: () => {
            resetRelatedCareTeamMemberCache();
            notify('success', intl('removeRelatedCareTeamMemberSuccess'));
        },
        onError: () => {
            notify('error', intl('removeRelatedCareTeamMemberFailure'));
        },
    });

    const addRelatedCareTeamMember = useMutation({
        mutationFn: async (relatedCareTeamMemberId: string) => {
            if (!careTeamMemberId) {
                throw new Error('careTeamMemberId is required');
            }

            return await appAPI.post(
                urls.api.careTeamMembers.relatedCareTeamMembers(medicalProfileId, careTeamMemberId),
                {
                    body: {
                        targetCareTeamMemberId: relatedCareTeamMemberId,
                    } as APITypes.medicalProfileService.AddCareTeamMemberRelationshipRequest,
                },
            );
        },
        onSuccess: () => {
            resetRelatedCareTeamMemberCache();
            notify('success', intl('addRelatedCareTeamMemberSuccess'));
            onAddRelatedCareTeamMemberSuccess?.();
        },
        onError: () => {
            notify('error', intl('addRelatedCareTeamMemberFailure'));
        },
    });

    const copyCareTeamMember = useMutation({
        mutationFn: async (destinationMedicalProfile: string) => {
            if (!careTeamMemberId) {
                throw new Error('careTeamMemberId is required');
            }

            return await appAPI.post<APITypes.medicalProfileService.CopyCareTeamMemberResponse>(
                urls.api.careTeamMembers.copy(medicalProfileId, careTeamMemberId),
                {
                    body: {
                        destinationMedicalProfileId: destinationMedicalProfile,
                    },
                },
            );
        },
        onSuccess: (response) => {
            notify('success', intl('copyCareTeamMemberSuccess'));
            onCopyCareTeamMemberSuccess?.(response.careTeamMemberId);
        },
        onError: () => {
            notify('error', intl('copyCareTeamMemberFailure'));
        },
    });

    return {
        careTeamMember: careTeamMemberQuery.data,
        isLoadingCareTeamMember: careTeamMemberQuery.isLoading,
        updateCareTeamMember: updateCareTeamMember.mutate,
        isUpdatingCareTeamMember: updateCareTeamMember.isPending,
        relatedCareTeamMembers: relatedCareTeamMembersQuery.data,
        isLoadingRelatedCareTeamMembers: relatedCareTeamMembersQuery.isLoading,
        removeRelatedCareTeamMember: removeRelatedCareTeamMember.mutate,
        isRemovingRelatedCareTeamMember: removeRelatedCareTeamMember.isPending,
        addRelatedCareTeamMember: addRelatedCareTeamMember.mutate,
        isAddingRelatedCareTeamMember: addRelatedCareTeamMember.isPending,
        copyCareTeamMember: copyCareTeamMember.mutate,
        isCopyingCareTeamMember: copyCareTeamMember.isPending,
    };
}
